import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DynamicStudyForm from '../components/study/DynamicStudyForm';
import { studyApi, entryApi } from '../utils/studyApiService';
import './StudyFormPage.css';

const StudyEntryFormPage = () => {
  const navigate = useNavigate();
  const { studyId, entryId } = useParams();
  const isEdit = !!entryId;

  const [study, setStudy] = useState(null);
  const [entryDate, setEntryDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [content, setContent] = useState({});
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const loadStudy = useCallback(async () => {
    try {
      const res = await studyApi.get(studyId);
      if (res.success) setStudy(res.data);
    } catch (e) {
      setError('스터디 정보를 불러오지 못했습니다.');
    }
  }, [studyId]);

  const loadExistingEntry = useCallback(async () => {
    if (!entryId) return;
    try {
      const res = await entryApi.get(studyId, entryId);
      if (res.success) {
        const entry = res.data;
        setEntryDate(String(entry.entry_date || '').slice(0, 10));
        setContent(entry.content || {});
        setNote(entry.note || '');
      }
    } catch (e) {
      setError('엔트리를 불러오지 못했습니다.');
    }
  }, [studyId, entryId]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([loadStudy(), loadExistingEntry()]);
      setLoading(false);
    };
    load();
  }, [loadStudy, loadExistingEntry]);

  const handleContentChange = (key, value) => {
    setContent((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!entryDate) { setError('날짜를 입력해주세요.'); return; }

    setSubmitting(true);
    setError('');
    try {
      if (isEdit) {
        await entryApi.update(studyId, entryId, {
          entry_date: entryDate,
          content,
          note: note || undefined,
        });
      } else {
        await entryApi.create(studyId, {
          entry_date: entryDate,
          content,
          note: note || undefined,
        });
      }
      navigate(`/studies/${studyId}`);
    } catch (e) {
      setError(e.message || '저장에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="sfp-loading">불러오는 중...</div>;
  }

  const fields = study?.template?.schema?.fields || [];

  return (
    <div className="sfp-page">
      <div className="sfp-header">
        <button className="sfp-back-btn" onClick={() => navigate(`/studies/${studyId}`)}>← 뒤로가기</button>
        <h1 className="sfp-title">
          {isEdit ? '엔트리 수정' : '새 엔트리'}
          {study && <span style={{ fontSize: '14px', fontWeight: 400, color: '#6b7280', marginLeft: 8 }}>— {study.title}</span>}
        </h1>
      </div>

      {error && <div className="sfp-error">{error}</div>}

      <form className="sfp-form" onSubmit={handleSubmit}>
        {/* 날짜 */}
        <div className="sfp-field">
          <label className="sfp-label">날짜 <span className="sfp-required">*</span></label>
          <input
            type="date"
            className="sfp-input sfp-date"
            value={entryDate}
            onChange={(e) => setEntryDate(e.target.value)}
            required
          />
        </div>

        {/* 템플릿 필드 */}
        {fields.length > 0 && (
          <div className="sfp-section">
            <div className="sfp-section-title">─── {study?.template?.name} 필드</div>
            <DynamicStudyForm
              fields={fields}
              value={content}
              onChange={handleContentChange}
            />
          </div>
        )}

        {/* 메모 */}
        <div className="sfp-field">
          <label className="sfp-label">메모</label>
          <textarea
            className="sfp-textarea"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="자유롭게 메모를 입력하세요 (선택)"
            rows={4}
          />
        </div>

        {/* 버튼 */}
        <div className="sfp-actions">
          <button
            type="button"
            className="sfp-btn sfp-btn-cancel"
            onClick={() => navigate(`/studies/${studyId}`)}
          >
            취소
          </button>
          <button
            type="submit"
            className="sfp-btn sfp-btn-submit"
            disabled={submitting}
          >
            {submitting ? '저장 중...' : (isEdit ? '수정 완료' : '작성 완료')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default StudyEntryFormPage;
